/**
 * The VAT breakdown, one row per rate.
 *
 * Austrian invoices routinely carry two rates (20 % on the service, 10 % on the travel), so this
 * is a list, not a single field. Typing a net amount fills the tax for you, but the tax stays
 * editable: what the supplier printed wins over what the arithmetic says.
 */
import { Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AUSTRIAN_VAT_RATES, totalsFromLines, vatFromNet, type VatLine } from "@/lib/at/vat";
import { formatMoney, formatRate, parseDecimal } from "@/lib/utils/format";

export function VatLinesField({
  value,
  onChange,
  currency = "EUR",
  disabled = false,
  totals = totalsFromLines(value),
}: {
  value: VatLine[];
  onChange: (lines: VatLine[]) => void;
  currency?: string;
  disabled?: boolean;
  /** What the footer shows; the lines' sum unless the document carries totals without lines. */
  totals?: ReturnType<typeof totalsFromLines>;
}) {
  const { t } = useTranslation();

  const update = (index: number, changes: Partial<VatLine>) => {
    onChange(
      value.map((line, position) => {
        if (position !== index) return line;
        const next = { ...line, ...changes };
        // Changing the rate or the net recomputes the tax; an explicit tax is left alone.
        if (changes.vat === undefined) next.vat = vatFromNet(next.net, next.rate);
        return next;
      }),
    );
  };

  return (
    <div className="space-y-2" data-testid="vat-lines">
      <div className="flex items-center justify-between">
        <Label>{t("document.vatBreakdown")}</Label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={() => onChange([...value, { rate: 20, net: 0, vat: 0 }])}
          data-testid="add-vat-line"
        >
          <Plus /> {t("document.addRate")}
        </Button>
      </div>

      {value.length === 0 && (
        <p className="text-sm text-muted-foreground">{t("document.noVatLines")}</p>
      )}

      {value.map((line, index) => (
        <div key={index} className="flex items-end gap-2">
          <div className="w-24 shrink-0 sm:w-28">
            <Label className="text-xs text-muted-foreground" htmlFor={`rate-${index}`}>
              {t("document.rate")}
            </Label>
            <Select
              disabled={disabled}
              value={String(line.rate)}
              onValueChange={(rate) => update(index, { rate: Number(rate) })}
            >
              <SelectTrigger id={`rate-${index}`} className="num">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AUSTRIAN_VAT_RATES.map((rate) => (
                  <SelectItem key={rate} value={String(rate)}>
                    {formatRate(rate)}
                  </SelectItem>
                ))}
                {!AUSTRIAN_VAT_RATES.includes(line.rate as never) && (
                  <SelectItem value={String(line.rate)}>{formatRate(line.rate)}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-0 flex-1">
            <Label className="text-xs text-muted-foreground" htmlFor={`net-${index}`}>
              {t("document.net")}
            </Label>
            <Input
              id={`net-${index}`}
              inputMode="decimal"
              className="text-right num"
              disabled={disabled}
              defaultValue={line.net ? String(line.net).replace(".", ",") : ""}
              onBlur={(event) => update(index, { net: parseDecimal(event.target.value) ?? 0 })}
            />
          </div>
          <div className="min-w-0 flex-1">
            <Label className="text-xs text-muted-foreground" htmlFor={`vat-${index}`}>
              {t("document.vat")}
            </Label>
            <Input
              id={`vat-${index}`}
              inputMode="decimal"
              className="text-right num"
              disabled={disabled}
              key={`${index}-${line.vat}`}
              defaultValue={line.vat ? String(line.vat).replace(".", ",") : ""}
              onBlur={(event) => update(index, { vat: parseDecimal(event.target.value) ?? 0 })}
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            aria-label={t("document.removeRate", { rate: line.rate })}
            onClick={() => onChange(value.filter((_, position) => position !== index))}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      ))}

      <div className="flex flex-wrap justify-end gap-x-6 gap-y-1 border-t pt-2 text-sm">
        <span className="text-muted-foreground">
          {t("document.net")}{" "}
          <span className="text-foreground num">{formatMoney(totals.netTotal, currency)}</span>
        </span>
        <span className="text-muted-foreground">
          {t("document.vat")}{" "}
          <span className="text-foreground num">{formatMoney(totals.vatTotal, currency)}</span>
        </span>
        <span className="font-medium">
          {t("document.gross")}{" "}
          <span className="num">{formatMoney(totals.grossTotal, currency)}</span>
        </span>
      </div>
    </div>
  );
}
